// Domain Entities
export class PricingTier {
  constructor(
    public readonly id: string,
    public readonly name: string,
    public readonly description: string,
    public readonly targetAudience: string,
    public readonly features: ReadonlyArray<string>,
    public readonly actionUrl: string,
    public readonly actionText: string,
    public readonly displayOrder: number
  ) {
    this.validateTier();
  }


  private validateTier(): void {
    if (!this.id || this.id.trim().length === 0) {
      throw new Error('Pricing tier must have a valid ID');
    }
    
    if (!this.name || this.name.trim().length === 0) {
      throw new Error('Pricing tier must have a valid name');
    }
    
    if (this.features.length === 0) {
      throw new Error('Pricing tier must have at least one feature');
    }
  }


  public hasFeature(featureName: string): boolean {
    return this.features.includes(featureName);
  }


  public isBasicTier(): boolean {
    return this.features.length === 1;
  }


  public isPremiumTier(): boolean {
    return this.features.length >= 5;
  }
}


export class PricingConfiguration {
  constructor(
    public readonly tiers: ReadonlyArray<PricingTier>,
    public readonly allAvailableFeatures: ReadonlyArray<string>
  ) {
    this.validateConfiguration();
  }


  private validateConfiguration(): void {
    if (this.tiers.length === 0) {
      throw new Error('Pricing configuration must have at least one tier');
    }


    const tierIds = this.tiers.map(tier => tier.id);
    const uniqueIds = new Set(tierIds);
    if (tierIds.length !== uniqueIds.size) {
      throw new Error('Pricing tiers must have unique IDs');
    }
  }


  public getTierById(id: string): PricingTier | undefined {
    return this.tiers.find(tier => tier.id === id);
  }


  public getTiersSortedByOrder(): ReadonlyArray<PricingTier> {
    return [...this.tiers].sort((a, b) => a.displayOrder - b.displayOrder);
  }


  public getBasicTier(): PricingTier | undefined {
    return this.tiers.find(tier => tier.isBasicTier());
  }


  public getPremiumTiers(): ReadonlyArray<PricingTier> {
    return this.tiers.filter(tier => tier.isPremiumTier());
  }
}


// Request/Response Models
export interface GetPricingRequest {
  readonly includeHiddenTiers?: boolean;
  readonly filterByFeature?: string;
}


export interface PricingResponse {
  readonly configuration: PricingConfiguration;
  readonly requestedAt: Date;
}


// Boundary Interfaces
export interface PricingInputBoundary {
  getPricingConfiguration(request: GetPricingRequest): Promise<void>;
}


export interface PricingOutputBoundary {
  presentPricingConfiguration(response: PricingResponse): void;
  presentPricingError(error: string): void;
}


export interface PricingRepository {
  getPricingConfiguration(): Promise<PricingConfiguration>;
}


